import { useState } from 'react';
import {
  formatPence,
  type ChildView,
  type FamilyAskView,
  type FamilyContent,
  type FamilyReply,
  type RelativeView,
} from '@london-life/contracts';
import { api } from '../lib/api';
import { actionKey, londonWhen } from '../lib/format';
import { needColour } from '../play/needs';
import { NameAsk } from './name-ask';

type Run = (work: () => Promise<FamilyReply>) => Promise<boolean>;

// Your children, as Lagos Life's babies: a baby on the way shows when it arrives; once here each child has a
// milestone, a care bar (fed keeps them full for half a day), Feed (paid) and Play (every few minutes), and a
// new name now and then (the other parent has a day to say no).
export function ChildrenList({
  family,
  reply,
  busy,
  run,
}: {
  family: FamilyContent;
  reply: FamilyReply;
  busy: boolean;
  run: Run;
}) {
  const [renaming, setRenaming] = useState<string | null>(null);
  if (!reply.children.length) return null;
  return (
    <>
      <strong className="business-step-title">YOUR CHILDREN</strong>
      <ul className="staff-list family-children" aria-label="Your children">
        {reply.children.map((child) => (
          <ChildCard
            key={child.id}
            child={child}
            family={family}
            wallet={reply.walletPence}
            renameAsk={reply.asks.find((ask) => ask.kind === 'rename' && ask.childId === child.id)}
            busy={busy}
            renaming={renaming === child.id}
            onRename={(open) => setRenaming(open ? child.id : null)}
            run={run}
          />
        ))}
      </ul>
    </>
  );
}

function ChildCard({
  child,
  family,
  wallet,
  renameAsk,
  busy,
  renaming,
  onRename,
  run,
}: {
  child: ChildView;
  family: FamilyContent;
  wallet: number;
  // a new name waiting for an answer (asked by you, or of you)
  renameAsk?: FamilyAskView;
  busy: boolean;
  renaming: boolean;
  onRename: (open: boolean) => void;
  run: Run;
}) {
  const babies = family.babies;
  const care = (action: 'feed' | 'play') => () =>
    void run(() => api.family.care(child.id, action, actionKey(`child-${action}`)));
  if (!child.born)
    return (
      <li className="family-child coming">
        <span aria-hidden="true">🍼</span>
        <span>
          <strong>{child.name} · on the way</strong>
          <small>
            With {child.parent.name} · arrives {londonWhen(child.bornAt)}
          </small>
        </span>
      </li>
    );
  return (
    <li className="family-child">
      <span aria-hidden="true">{child.milestone.emoji}</span>
      <span>
        <strong>
          {child.name} · {child.milestone.label}
        </strong>
        <small>
          {child.origin === 'adopted' ? 'Adopted with' : 'With'} {child.parent.name} ·{' '}
          {child.age === 0 ? 'arrived today' : `${child.age} ${child.age === 1 ? 'day' : 'days'} old`}
        </small>
        <span className="family-care">
          <span
            className="needs-bar"
            role="meter"
            aria-label={`${child.name}'s care`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={child.care}
          >
            <b style={{ width: `${child.care}%`, background: needColour(child.care) }} />
          </span>
          <small>{child.hungry ? 'Hungry' : `${child.care}% full`}</small>
        </span>
        <span className="cars-actions">
          {child.feedFrom ? (
            <small className="family-done">✓ Fed · hungry again {londonWhen(child.feedFrom)}</small>
          ) : (
            <button
              type="button"
              className="flights-terminal"
              disabled={busy || wallet < babies.feedPence}
              onClick={care('feed')}
            >
              🍼 Feed · {formatPence(babies.feedPence)}
            </button>
          )}
          {child.playFrom ? (
            <small className="family-done">✓ Played · again {londonWhen(child.playFrom)}</small>
          ) : (
            <button type="button" className="flights-terminal" disabled={busy} onClick={care('play')}>
              🧸 Play
            </button>
          )}
          {child.renaming ? (
            <small className="family-done">
              {renameAsk && !renameAsk.mine
                ? `✏️ ${child.parent.name} asked to call ${child.name} ${renameAsk.babyName}: answer above`
                : `✏️ A new name is waiting for ${child.parent.name}`}
            </small>
          ) : child.renameFrom ? (
            <small className="family-done">✏️ New name again {londonWhen(child.renameFrom)}</small>
          ) : (
            <button
              type="button"
              className="flights-terminal"
              aria-expanded={renaming}
              onClick={() => onRename(!renaming)}
            >
              ✏️ New name…
            </button>
          )}
        </span>
        {renaming && (
          <NameAsk
            label={`A NEW NAME FOR ${child.name.toUpperCase()}`}
            placeholder={`New name for ${child.name}`}
            hint={`${child.parent.name} has ${babies.renameAnswerHours} hours to say no`}
            cta={`Ask ${child.parent.name}`}
            busy={busy}
            onAsk={(name) =>
              void run(() =>
                api.family.ask(
                  { kind: 'rename', lifeId: child.parent.lifeId, childId: child.id, name },
                  actionKey('family'),
                ),
              ).then((done) => done && onRename(false))
            }
          />
        )}
      </span>
    </li>
  );
}

// Your relatives: players who agreed in People to be your sibling, cousin and so on; either side can remove it
export function RelativesList({
  family,
  relatives,
  busy,
  onRemove,
}: {
  family: FamilyContent;
  relatives: RelativeView[];
  busy: boolean;
  onRemove: (relative: RelativeView) => void;
}) {
  const [removing, setRemoving] = useState<string | null>(null);
  if (!relatives.length) return null;
  return (
    <>
      <strong className="business-step-title">YOUR RELATIVES</strong>
      <ul className="staff-list" aria-label="Your relatives">
        {relatives.map((relative) => {
          const kin = family.kin.find((item) => item.kind === relative.kin);
          const name = relative.username ? `@${relative.username}` : relative.firstName;
          return (
            <li key={relative.lifeId}>
              <span aria-hidden="true">{kin?.emoji ?? '🏡'}</span>
              <span>
                <strong>
                  {relative.firstName} · {kin?.label ?? 'Family'}
                </strong>
                <small>{name}</small>
                {removing === relative.lifeId ? (
                  <span className="jet-sell">
                    <span>Remove {relative.firstName} as a relative?</span>
                    <button type="button" onClick={() => setRemoving(null)} disabled={busy}>
                      Keep
                    </button>
                    <button
                      type="button"
                      className="jet-sell-yes"
                      disabled={busy}
                      onClick={() => {
                        onRemove(relative);
                        setRemoving(null);
                      }}
                    >
                      Remove
                    </button>
                  </span>
                ) : (
                  <button type="button" className="flights-terminal" onClick={() => setRemoving(relative.lifeId)}>
                    Remove…
                  </button>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}
